import { useState } from "react";
import {
  ArrowUpRight,
  Check,
  Sparkles,
  Infinity as InfinityIcon,
  ShieldCheck,
  Zap,
  Server,
  Globe,
  MessageSquare,
  Instagram,
  MapPin,
  X,
} from "lucide-react";

/* ---------- Data ---------- */
const plans = [
  {
    id: "care",
    kicker: "Numedia Care",
    subtitle: "Je continue à m'occuper de votre site",
    price: "69 €",
    period: "/mois HTVA",
    tagline: "Votre site vit, sans que vous ayez à y penser.",
    highlights: [
      { Icon: Server, label: "Hébergement européen performant" },
      { Icon: Globe, label: "Nom de domaine renouvelé chaque année" },
      {
        Icon: InfinityIcon,
        label: "Modifications & ajouts illimités",
        detail: "Traités sous 48 h · 1 demande à la fois",
      },
      { Icon: ShieldCheck, label: "Mises à jour techniques & sécurité" },
      { Icon: Zap, label: "Sauvegardes quotidiennes" },
      { Icon: MessageSquare, label: "Support prioritaire sous 24 h ouvrées" },
    ],
    ctaLabel: "Réserver un appel",
    featured: false,
  },
  {
    id: "care-plus",
    kicker: "Numedia Care+",
    subtitle: "Je m'occupe aussi de votre visibilité",
    price: "149 €",
    period: "/mois HTVA",
    tagline: "Votre marque, visible et animée partout — chaque mois.",
    highlights: [
      { Icon: Sparkles, label: "Tout Numedia Care inclus" },
      { Icon: MapPin, label: "Fiche Google Business tenue à jour" },
      {
        Icon: Instagram,
        label: "Publications réseaux sociaux",
        detail: "4 publications / mois sur le compte de votre choix",
      },
      {
        Icon: MessageSquare,
        label: "Rapport mensuel de visibilité",
        detail: "Google, RS, trafic — synthèse actionnable",
      },
    ],
    ctaLabel: "Réserver un appel",
    featured: true,
    featuredLabel: "Le partenaire complet",
  },
];

const comparison = [
  {
    label: "Faire soi-même (Wix / Squarespace)",
    price: "≈ 30 €/mois",
    summary: "Heures perdues à chaque modif · aucun support · SEO limité",
    cons: [
      "Vous perdez plusieurs heures à chaque modif",
      "Aucun support humain",
      "Design générique · SEO limité",
    ],
    positive: false,
  },
  {
    label: "Freelance ponctuel",
    price: "50 € – 90 €/h",
    summary: "Devis à chaque demande · pas de suivi long terme",
    cons: [
      "Devis + délai à chaque demande",
      "Pas de suivi long terme",
      "Vous restez seul entre deux interventions",
    ],
    positive: false,
  },
  {
    label: "Numedia — abonnement mensuel",
    price: "69 €/mois",
    summary: "Modifications illimitées · un partenaire, pas un prestataire",
    cons: [
      "Modifications illimitées, traitées sous 48 h",
      "Un seul interlocuteur, sur la durée",
      "Site sécurisé, sauvegardé, visible",
    ],
    positive: true,
  },
];

/* ---------- Component ---------- */
export default function Subscription() {
  return (
    <section
      id="abonnement"
      data-testid="subscription-section"
      className="relative py-20 md:py-28 overflow-hidden bg-[#0B1027] text-white"
    >
      {/* Aurora blobs — soft background depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 40% at 15% 20%, rgba(99,102,241,0.18) 0%, transparent 60%), radial-gradient(50% 40% at 85% 80%, rgba(236,72,153,0.15) 0%, transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        {/* Header — emotional + positioning */}
        <div className="max-w-3xl">
          <span className="eyebrow text-white/70">— Accompagnement Numedia</span>
          <h2
            data-testid="subscription-heading"
            className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium mt-5 leading-[1.05] text-white"
          >
            Votre site ne s'arrête pas{" "}
            <em className="not-italic italic font-light text-aurora">
              le jour de sa mise en ligne.
            </em>
          </h2>
          <p className="mt-6 text-white/70 leading-relaxed text-base md:text-lg max-w-xl">
            Une fois votre site livré, deux options s'offrent à vous : soit vous
            en prenez la gestion vous-même, soit vous me laissez continuer à
            m'en occuper — pour qu'il reste vivant, visible et à jour, mois
            après mois.
          </p>
        </div>

        {/* Plans — Mobile: tab switcher · Desktop: 2-column grid */}
        <PlansSection plans={plans} />

        {/* Compact engagement strip — 1 line, key reassurance signals */}
        <CompactEngagementStrip />

        {/* Mini comparison — 3-row table vs alternatives */}
        <MiniComparison />

        {/* Final CTA — kept in all variants */}
        <div className="mt-14 md:mt-20 text-center">
          <p className="text-white/70 text-base md:text-lg mb-6 max-w-2xl mx-auto">
            Prêt à passer d'un site "que je paie chaque année sans savoir
            pourquoi" à un site qui{" "}
            <em className="not-italic italic text-aurora font-medium">
              travaille vraiment pour vous
            </em>{" "}
            ?
          </p>
          <a
            href="#reservation"
            data-testid="subscription-final-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full btn-aurora text-white font-semibold px-8 h-14 text-base shadow-tactile-lg hover-lift transition group"
          >
            Réserver un appel de 30 min
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
          <p className="mt-4 text-white/50 text-xs">
            Gratuit · sans engagement · vous repartez avec un plan clair.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- Sub-components ---------- */

function PlansSection({ plans }) {
  // Mobile: tab switcher · Desktop: 2-column grid
  const defaultId = plans.find((p) => p.featured)?.id || plans[0].id;
  const [activeId, setActiveId] = useState(defaultId);
  const activePlan = plans.find((p) => p.id === activeId) || plans[0];

  return (
    <div className="mt-14 md:mt-20" data-testid="subscription-plans">
      {/* Mobile: tab switcher (hidden on md+) */}
      <div className="md:hidden">
        <div
          role="tablist"
          aria-label="Choisir un abonnement"
          data-testid="subscription-plans-tabs"
          className="relative mx-auto mb-6 grid grid-cols-2 gap-1 p-1 rounded-full"
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.10)",
            maxWidth: "22rem",
          }}
        >
          {plans.map((p) => {
            const active = p.id === activeId;
            return (
              <button
                key={p.id}
                role="tab"
                aria-selected={active}
                data-testid={`subscription-plans-tab-${p.id}`}
                onClick={() => setActiveId(p.id)}
                className="relative z-10 h-11 rounded-full text-[13px] font-semibold transition-colors duration-300"
                style={{
                  color: active ? "#fff" : "rgba(255,255,255,0.65)",
                  background: active
                    ? "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)"
                    : "transparent",
                }}
              >
                {p.kicker}
              </button>
            );
          })}
        </div>
        <PlanCard plan={activePlan} />
      </div>

      {/* Desktop: 2-column grid */}
      <div className="hidden md:grid md:grid-cols-2 md:gap-8 items-stretch">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} />
        ))}
      </div>
    </div>
  );
}

function PlanCard({ plan }) {
  const featured = !!plan.featured;
  return (
    <article
      data-testid={`subscription-plan-${plan.id}`}
      className={`relative flex flex-col rounded-3xl p-7 md:p-8 backdrop-blur-md h-full ${
        featured ? "md:-translate-y-2" : ""
      }`}
      style={{
        background: featured
          ? "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)"
          : "rgba(255,255,255,0.04)",
        border: featured
          ? "1px solid rgba(255,255,255,0.20)"
          : "1px solid rgba(255,255,255,0.10)",
      }}
    >
      {/* Aurora border for featured */}
      {featured && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-3xl"
          style={{
            padding: "1.5px",
            background:
              "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)",
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }}
        />
      )}

      {/* Featured pill */}
      {featured && (
        <div
          data-testid={`subscription-plan-${plan.id}-featured-badge`}
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-white font-bold shadow-tactile"
          style={{
            background:
              "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)",
          }}
        >
          <Sparkles size={11} className="text-white" />
          {plan.featuredLabel}
        </div>
      )}

      {/* Kicker + subtitle */}
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">
          {plan.subtitle}
        </div>
        <h3
          data-testid={`subscription-plan-${plan.id}-title`}
          className="font-display text-2xl md:text-3xl tracking-tight font-medium text-white mt-2 leading-tight"
        >
          {plan.kicker}
        </h3>
        <p className="mt-3 font-display italic text-aurora text-base md:text-lg leading-snug">
          {plan.tagline}
        </p>
      </div>

      {/* Price block */}
      <div className="relative mt-6 pt-6 border-t border-white/10">
        <div className="flex items-baseline gap-2">
          <span
            data-testid={`subscription-plan-${plan.id}-price`}
            className="font-display text-5xl md:text-6xl font-medium text-white leading-none"
          >
            {plan.price}
          </span>
          <span className="text-sm text-white/60">{plan.period}</span>
        </div>
        <div className="mt-2 text-xs text-white/50">
          Sans engagement · résiliable avec préavis de 30 jours
        </div>
      </div>

      {/* Highlights */}
      <ul className="relative mt-6 space-y-3.5 flex-1">
        {plan.highlights.map(({ Icon, label, detail }) => (
          <li key={label} className="flex items-start gap-3">
            <div
              className="flex items-center justify-center h-8 w-8 rounded-full shrink-0"
              style={{
                background:
                  "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)",
              }}
            >
              <Icon size={14} className="text-white" strokeWidth={2.2} />
            </div>
            <div className="flex-1 pt-0.5">
              <div className="text-sm md:text-[15px] text-white font-medium leading-snug">
                {label}
              </div>
              {detail && (
                <div className="text-xs text-white/55 mt-1 leading-snug">
                  {detail}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="relative mt-7 pt-6 border-t border-white/10">
        <a
          href="#reservation"
          data-testid={`subscription-plan-${plan.id}-cta`}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full btn-aurora text-white font-semibold px-5 h-12 text-sm shadow-tactile hover-lift transition whitespace-nowrap group/cta"
        >
          {plan.ctaLabel}
          <ArrowUpRight
            size={16}
            className="transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
          />
        </a>
      </div>
    </article>
  );
}

function CompactEngagementStrip() {
  return (
    <div
      data-testid="engagement-strip-compact"
      className="mt-8 md:mt-10 rounded-full px-5 md:px-6 py-3 md:py-3.5 backdrop-blur-md flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 text-sm text-white/80"
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.10)",
      }}
    >
      <span className="inline-flex items-center gap-1.5">
        <ShieldCheck size={14} className="text-aurora" />
        Sans engagement · préavis 30 j
      </span>
      <span className="hidden md:inline text-white/25">·</span>
      <span className="inline-flex items-center gap-1.5">
        <Sparkles size={14} className="text-aurora" />
        Paiement annuel : <strong className="text-white">2 mois offerts</strong>
      </span>
    </div>
  );
}

function MiniComparison() {
  return (
    <div
      data-testid="subscription-comparison"
      className="mt-14 md:mt-20"
    >
      <div className="max-w-2xl mb-6">
        <span className="eyebrow text-white/70">— Le comparatif honnête</span>
        <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium mt-3 leading-[1.1] text-white">
          Vos alternatives, en un coup d'œil.
        </h3>
      </div>
      <div
        className="rounded-2xl backdrop-blur-md overflow-hidden"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        {comparison.map((c, i) => {
          const positive = c.positive;
          return (
            <div
              key={c.label}
              className={`flex flex-col md:flex-row md:items-center gap-2 md:gap-6 px-5 md:px-6 py-4 ${
                i < comparison.length - 1 ? "border-b border-white/10" : ""
              } ${positive ? "bg-white/[0.03]" : ""}`}
            >
              <div className="md:w-64 shrink-0 flex items-center gap-2.5">
                {positive ? (
                  <Check size={14} className="text-emerald-400 shrink-0" strokeWidth={2.5} />
                ) : (
                  <X size={14} className="text-white/40 shrink-0" strokeWidth={2.5} />
                )}
                <span
                  className={`text-sm font-medium leading-snug ${
                    positive ? "text-white" : "text-white/70"
                  }`}
                >
                  {c.label}
                </span>
              </div>
              <div className="md:w-24 shrink-0">
                <span
                  className={`text-sm font-display font-medium ${
                    positive ? "text-aurora" : "text-white/50"
                  }`}
                >
                  {c.price}
                </span>
              </div>
              <div
                className={`text-[13px] leading-snug flex-1 ${
                  positive ? "text-white/85" : "text-white/50"
                }`}
              >
                {c.summary}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
